// Executive Office proposal grouping.
// Turns multiple related pending proposals into one operational decision.
//
// Grouping rule: proposals share the same operational objective when they
// touch the same *stable target* (person, class, family, vendor, or payroll
// period) AND fall inside the same operational category. Unrelated proposals
// are never merged just because they arrived together.
import type { Proposal } from "@/components/assistant/ProposedChangeCard";

export type GroupCategory =
  | "Instructor Onboarding"
  | "Payroll"
  | "Family Registration"
  | "Class Launch"
  | "Vendor Setup"
  | "Semester Transition"
  | "Marketing"
  | "Office Settings"
  | "Office";

export type StepState =
  | "Ready"
  | "Needs Your Choice"
  | "Waiting on Information"
  | "Already Complete"
  | "Couldn't Finish Yet";

export interface GroupStep {
  proposal: Proposal;
  outcome: string;   // outcome-first label, e.g. "Assign a class"
  state: StepState;
  waitingReason?: string;
}

export interface ProposalGroup {
  key: string;
  category: GroupCategory;
  title: string;         // e.g. "Finish Kohl's Onboarding"
  subject: string;       // person/class/family/vendor name
  why: string;           // why MAVIS grouped these
  steps: GroupStep[];
  readyIds: string[];    // pending_ids that Approve Ready Steps will execute
  counts: {
    ready: number;
    waiting: number;
    needsChoice: number;
    complete: number;
    blocked: number;
  };
  recommendation: string;
}

// ---------- Category resolution ----------

function categoryFor(p: Proposal): GroupCategory {
  const kind = (p.target_kind || p.target_table || "").toLowerCase();
  const action = (p.action_kind || "").toLowerCase();
  if (kind.includes("instructor") || action.includes("instructor"))
    return "Instructor Onboarding";
  if (kind.includes("payroll") || action.includes("payroll") || action === "one_time_payment")
    return "Payroll";
  if (kind.includes("registration") || kind.includes("class_payment") || action.includes("registration"))
    return "Family Registration";
  if (kind.includes("period")) return "Semester Transition";
  if (kind.includes("class")) return "Class Launch";
  if (kind.includes("vendor")) return "Vendor Setup";
  if (kind.includes("email") || kind.includes("marketing")) return "Marketing";
  if (kind.includes("knowledge") || kind.includes("memory") || kind.includes("configuration") || kind.includes("settings"))
    return "Office Settings";
  return "Office";
}

// ---------- Step outcome + state ----------

function outcomeFor(p: Proposal): string {
  const action = (p.action_kind || "").toLowerCase();
  const kind = (p.target_kind || p.target_table || "").toLowerCase();
  if (action === "resend_email") return "Resend confirmation email";
  if (action === "one_time_payment" || kind.includes("payroll"))
    return "Record payroll payment";
  if (action.includes("create_instructor")) return "Create the instructor profile";
  if (action.includes("create_assignment") || (kind.includes("instructor") && action.includes("assign")))
    return "Assign a class";
  if (action.includes("merge")) return "Merge duplicate profiles";
  if (kind.includes("period") && action.includes("create")) return "Create the registration period";
  if (kind.includes("class") && action.includes("schedule")) return "Update the schedule";
  if (kind.includes("class") && action.includes("create")) return "Publish the class";
  if (kind.includes("class")) return "Update the class";
  if (kind.includes("registration")) return "Update the registration";
  if (kind.includes("vendor")) return "Set up the vendor";
  if (kind.includes("knowledge") || kind.includes("memory")) return "Save a note";
  return "Update the record";
}

const WAITING_HINT = /(waiting|awaiting|needs?\s+(?:document|w-?9|packet|upload|signature|info)|missing|pending upload|not yet received)/i;
const CHOICE_HINT = /(choose|select|which|pick|decide|confirm which)/i;
const COMPLETE_HINT = /(already complete|already done|already recorded|no changes needed)/i;
const FAILED_HINT = /(couldn't|could not|failed|error|blocked)/i;

function stateFor(p: Proposal): { state: StepState; waitingReason?: string } {
  const reason = p.reason || "";
  if (COMPLETE_HINT.test(reason)) return { state: "Already Complete" };
  if (FAILED_HINT.test(reason)) return { state: "Couldn't Finish Yet", waitingReason: reason };
  if (WAITING_HINT.test(reason)) return { state: "Waiting on Information", waitingReason: reason };
  if (CHOICE_HINT.test(reason)) return { state: "Needs Your Choice", waitingReason: reason };
  // Workflow steps: if any remaining step reads like it needs a document, mark waiting.
  const stepText = (p.workflow_steps || []).map((s) => s.label).join(" ");
  if (WAITING_HINT.test(stepText)) return { state: "Waiting on Information" };
  // Missing target — cannot execute safely.
  if (!p.target_id) return { state: "Needs Your Choice", waitingReason: "Target not confirmed." };
  return { state: "Ready" };
}

// ---------- Group key ----------

function groupKeyFor(p: Proposal): string | null {
  // Prefer a stable subject ID within a category. If we don't have a real
  // target_id, fall back to normalized target_label so a single named person
  // still coalesces (but never coalesces unrelated blank targets).
  const cat = categoryFor(p);
  const subject = (p.target_id && String(p.target_id).trim()) ||
    (p.target_label ? p.target_label.trim().toLowerCase() : "");
  if (!subject) return null;
  return `${cat}::${subject}`;
}

// ---------- Public: group + dedupe ----------

export interface GroupingResult {
  groups: ProposalGroup[];
  standalone: Proposal[];
  suppressed: number;   // duplicates removed
}

/**
 * Group proposals into operational decisions.
 * - Dedupe by (action_kind, target_id) — keep the newest.
 * - Coalesce by (category, subject).
 * - Groups with only one member are returned as standalone.
 */
export function groupProposals(input: Proposal[]): GroupingResult {
  // 1. Dedupe by semantic signature.
  const seen = new Map<string, Proposal>();
  let suppressed = 0;
  for (const p of input) {
    if (!p?.pending_id) continue;
    const sig = `${(p.action_kind || "").toLowerCase()}::${p.target_id || p.target_label || ""}`;
    if (seen.has(sig)) {
      suppressed += 1;
      continue;
    }
    seen.set(sig, p);
  }
  const deduped = Array.from(seen.values());

  // 2. Bucket by group key.
  const buckets = new Map<string, Proposal[]>();
  const orphans: Proposal[] = [];
  for (const p of deduped) {
    const key = groupKeyFor(p);
    if (!key) {
      orphans.push(p);
      continue;
    }
    const arr = buckets.get(key) ?? [];
    arr.push(p);
    buckets.set(key, arr);
  }

  const groups: ProposalGroup[] = [];
  const standalone: Proposal[] = [...orphans];

  for (const [key, items] of buckets) {
    if (items.length < 2) {
      standalone.push(...items);
      continue;
    }
    const category = categoryFor(items[0]);
    const subject = items[0].target_label || "this record";
    const steps: GroupStep[] = items.map((p) => {
      const { state, waitingReason } = stateFor(p);
      return { proposal: p, outcome: outcomeFor(p), state, waitingReason };
    });
    const counts = {
      ready: steps.filter((s) => s.state === "Ready").length,
      waiting: steps.filter((s) => s.state === "Waiting on Information").length,
      needsChoice: steps.filter((s) => s.state === "Needs Your Choice").length,
      complete: steps.filter((s) => s.state === "Already Complete").length,
      blocked: steps.filter((s) => s.state === "Couldn't Finish Yet").length,
    };
    const readyIds = steps.filter((s) => s.state === "Ready").map((s) => s.proposal.pending_id);
    groups.push({
      key,
      category,
      title: titleFor(category, subject),
      subject,
      why: whyFor(category, subject, counts),
      steps,
      readyIds,
      counts,
      recommendation: recommendationFor(counts),
    });
  }

  // 3. Sort groups by priority: needs decision → ready → blocked → recency.
  groups.sort((a, b) => priorityScore(b) - priorityScore(a));

  return { groups, standalone, suppressed };
}

function titleFor(category: GroupCategory, subject: string): string {
  switch (category) {
    case "Instructor Onboarding": return `Finish ${subject}'s Onboarding`;
    case "Payroll": return `Complete ${subject}'s Payroll`;
    case "Family Registration": return `Complete ${subject}'s Registration`;
    case "Class Launch": return `Launch ${subject}`;
    case "Vendor Setup": return `Prepare ${subject}`;
    case "Semester Transition": return `Transition ${subject} To Next Semester`;
    case "Marketing": return `Send ${subject}`;
    case "Office Settings": return `Update ${subject}`;
    default: return `Complete ${subject}`;
  }
}

function whyFor(category: GroupCategory, subject: string, c: ProposalGroup["counts"]): string {
  if (category === "Instructor Onboarding")
    return `${subject}'s profile is set up. These remaining steps will prepare them to teach.`;
  if (category === "Payroll")
    return `A few connected steps are needed to close out ${subject}'s payroll.`;
  if (category === "Family Registration")
    return `${subject}'s registration has a few steps left before it's finished.`;
  if (category === "Class Launch")
    return `${subject} is almost ready to open for sign-ups.`;
  if (c.waiting > 0)
    return `Some steps are ready and one is waiting on outside information.`;
  return `These items belong to the same objective, so I grouped them together.`;
}

function recommendationFor(c: ProposalGroup["counts"]): string {
  if (c.ready > 0 && c.waiting > 0)
    return "Complete the ready steps now, then leave anything requiring outside information in Waiting On.";
  if (c.ready > 0)
    return "The ready steps are safe to approve together.";
  if (c.waiting > 0)
    return "I'm waiting on outside information before I can move these forward.";
  if (c.needsChoice > 0)
    return "Open Review Individually to make the choices, then approve.";
  return "Review each step individually to decide what to do next.";
}

function priorityScore(g: ProposalGroup): number {
  return (
    g.counts.needsChoice * 100 +
    g.counts.ready * 50 +
    g.counts.blocked * 30 +
    g.counts.waiting * 10 +
    g.steps.length
  );
}
